import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Select from 'react-select'
import { hideModal } from '../../../../actions/_modal'
import Modal, { ModalTop, ModalBottom } from '../../../mini/Modal'
import FlexAlignCenter from '../../../styled/FlexAlignCenter'
import Checkbox from '../../../mini/Checkbox'
import {
  ModalWrapper,
  ModalSubheaderWrapper,
  FormInner,
  CancelButton,
  SaveButton,
  Divider,
  InputLabel,
  RadioLabel,
  RadioContainer,
  AllSectionsLabel,
  EditIcon,
  TextareaAutosizeStyled,
  selectStyles,
  InvalidUrlWarning,
  TextInput,
  InputTypography,
  RadioInput, MaxCharacters, MaxCharactersWrapper
} from './styled'
import FileUploader from './FileUploader'
import {
  addMentorResource,
  deleteMentorResource,
  patchMentorResource,
  patchMentorResourceFileShell
} from '../API'
import withApiData from '../../../HOC/withApiData'
import { isValidUrl } from '../../../../utils/string'
import {
  addJourneyResource, deleteJourneyMentorResource,
  patchJourneyMentorResource, patchJourneyResourceFileShell
} from '../../../MyCourses/Courses/API'
import useLexicon from '../../../../hooks/useLexicon'

const resourceTypeOptions = [
  { value: 'audio', label: 'Audio' },
  { value: 'document', label: 'Document' },
  { value: 'game_app', label: 'App' },
  { value: 'image', label: 'Image' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'video', label: 'Video' },
  { value: 'web', label: 'Web' },
  { value: 'simulation', label: 'Simulation' }
]

const fileResourceShell = {
  resource_type: 'document',
  name: 'Resource'
}

const defaultResource = {
  type: null,
  format: 'link',
  url: '',
  name: '',
  description: ''
}

const initialResourceMaker = r => () =>
  r
    ? {
      type: resourceTypeOptions.find(type => type.value === r.resource_type),
      format: r.file_url ? 'file' : 'link',
      url: r.url || '',
      name: r.name || '',
      description: r.description || ''
    }
    : defaultResource

const AddResourceModal = ({
                            apiData,
                            ids: { courseSectionId, levelId, activityId, questId, journeyId },
                            onMutation,
                            resourceToEdit
                          }) => {
  const isJourneyResource = journeyId !== undefined
  const [resource, setResource] = useState(initialResourceMaker(resourceToEdit))
  const [patchId, setPatchId] = useState(resourceToEdit && resourceToEdit.id)
  const [applyToAllSections, setApplyToAllSections] = useState(true)
  const [applyToAllSectionsLocked, setApplyToAllSectionsLocked] = useState(
    false
  )
  const [isUploading, setIsUploading] = useState(false)

  const dispatch = useDispatch()
  const getResourceToAdd = patching => {
    return {
      resource_type: resource.type.value,
      name: resource.name,
      description: resource.description,
      url: patching ? undefined : resource.url
    }
  }
  const lexicon = useLexicon()

  const isEditing = !!resourceToEdit
  const isPatching = !!patchId

  const changeHandler = e => {
    let value = e.target.value
    if (e.target.name === 'name' && e.target.value.length > 255) {
      // If it's a regular character insertion, don't allow it
      if (resource.name.length === 255) {
        return
      } else {
        // If it's pasted, cut off extra
        value = e.target.value.substring(0, 255)
      }
    }

    setResource({
      ...resource,
      [e.target.name]: value
    })

  }

  const onSave = async () => {
    try {
      const r = getResourceToAdd(isPatching)
      if (isPatching) {
        if(isJourneyResource) {
          await patchJourneyMentorResource(apiData, courseSectionId, patchId, r)
        } else {await patchMentorResource(apiData, courseSectionId, levelId, patchId, r)}
      } else {
        if(isJourneyResource) {
          await addJourneyResource(
            apiData,
            courseSectionId,
            r,
            false,
            applyToAllSections
          )
        } else {
          await addMentorResource(
            apiData,
            courseSectionId,
            levelId,
            activityId,
            questId,
            r,
            false,
            applyToAllSections
          )
        }
      }
      onMutation()
    } catch (e) {
      alert(`There was an issue adding this ${lexicon.resource}.`)
    }
    setResource([])
    dispatch(hideModal())
  }

  const makeFileResourceShell = async () => {
    setApplyToAllSectionsLocked(true)
    if(isJourneyResource) {
      return await addJourneyResource(
        apiData,
        courseSectionId,
        fileResourceShell,
        true,
        applyToAllSections
      )
    } else {
      return await addMentorResource(
        apiData,
        courseSectionId,
        levelId,
        activityId,
        questId,
        fileResourceShell,
        true,
        applyToAllSections
      )
    }
  }

  const patchFileResourceShell = async (resourceId, file) => {
    if(isJourneyResource) {
      return await patchJourneyResourceFileShell(
        apiData,
        courseSectionId,
        resourceId,
        file
      )
    } else {
      return await patchMentorResourceFileShell(
        apiData,
        courseSectionId,
        levelId,
        resourceId,
        file
      )
    }
  }

  const onCancelFileUpload = async resourceId => {
    setPatchId(null)
    if(isJourneyResource) {
      await deleteJourneyMentorResource(
        apiData,
        courseSectionId,
        resourceId,
        applyToAllSections
      )
    } else {
      await deleteMentorResource(
        apiData,
        courseSectionId,
        levelId,
        resourceId,
        applyToAllSections
      )
    }
    setApplyToAllSectionsLocked(false)
  }

  const onCancel = () => {
    if (isUploading || (isPatching && !isEditing)) {
      onCancelFileUpload(patchId)
    }
    dispatch(hideModal())
  }

  useEffect(() => {
    window.addEventListener('unload', onCancel)
    return () => {
      window.removeEventListener('unload', onCancel)
    }
  }, [isUploading, isPatching, isEditing])

  const applyToAllSectionsClickHandler = () => {
    if (!applyToAllSectionsLocked) {
      setApplyToAllSections(!applyToAllSections)
    }
  }

  const isInvalidUrl = resource.url && !isValidUrl(resource.url)

  const incomplete =
    !resource.type ||
    !resource.name.trim() ||
    (resource.format === 'link'
      ? !resource.url || isInvalidUrl
      : (!isPatching) || isUploading)


  return (
    <Modal>
      <ModalWrapper>
        <ModalTop>
          {isEditing ? (
            <FlexAlignCenter>
              <EditIcon />
              <h2 className='h2 lato light'>Edit {lexicon.resource}</h2>
            </FlexAlignCenter>
          ) : (
            <h2 className='h2 lato light'>+ Add {lexicon.resource}</h2>
          )}

          <div>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
            <SaveButton onClick={onSave} disabled={incomplete || isUploading}>
              {isUploading ? 'Upload in Progress' : 'Save'}
            </SaveButton>
          </div>
        </ModalTop>

        {!isEditing && (
          <ModalSubheaderWrapper>
            <Checkbox
              checked={applyToAllSections}
              disabled={applyToAllSectionsLocked}
              onChange={applyToAllSectionsClickHandler}
            />
            <AllSectionsLabel
              className='caption4'
              disabled={applyToAllSectionsLocked}
              onClick={applyToAllSectionsClickHandler}
            >
              Add to all my {lexicon.section.toLowerCase()}s.
            </AllSectionsLabel>
          </ModalSubheaderWrapper>
        )}

        <ModalBottom>
          <form style={{ padding: '0' }}>
            <FormInner>
              <InputLabel required>{lexicon.resource + ' Type'}</InputLabel>
              <Select
                options={resourceTypeOptions}
                placeholder={lexicon.resource + ' Type'}
                styles={selectStyles}
                isClearable={false}
                isSearchable={false}
                defaultValue={resource.type}
                onChange={selection =>
                  setResource({
                    ...resource,
                    type: selection
                  })
                }
              />

              <InputLabel>Format</InputLabel>
              {isPatching ? (
                <InputTypography capitalize>{resource.format}</InputTypography>
              ) : (
                <RadioContainer>
                  <RadioInput
                    name='format'
                    id='radio-link'
                    value={'link'}
                    checked={resource.format === 'link'}
                    onChange={changeHandler}
                  />
                  <RadioLabel for='radio-link'>Link</RadioLabel>

                  <RadioInput
                    name='format'
                    id='radio-file'
                    value={'file'}
                    checked={resource.format === 'file'}
                    onChange={changeHandler}
                  />
                  <RadioLabel for='radio-file'>File</RadioLabel>
                </RadioContainer>
              )}

              {resource.format === 'link' ? (
                <>
                  <InputLabel for='url' required>
                    URL
                  </InputLabel>
                  {isEditing ? (
                    <InputTypography>{resource.url}</InputTypography>
                  ) : (
                    <>
                      <TextInput
                        name='url'
                        id='url'
                        placeholder='URL'
                        value={resource.url}
                        onChange={changeHandler}
                        invalid={isInvalidUrl}
                      />
                      {isInvalidUrl && (
                        <InvalidUrlWarning>
                          Please ensure the URL contains either http:// or
                          https://.
                        </InvalidUrlWarning>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <InputLabel for='resource-file' required>
                    {lexicon.resource} File
                  </InputLabel>
                  <FileUploader
                    isEditing={isEditing}
                    fileName={resourceToEdit && resourceToEdit.file_name}
                    setPatchId={setPatchId}
                    onCancelUpload={onCancelFileUpload}
                    setIsUploading={setIsUploading}
                    makeShell={makeFileResourceShell}
                    patchShell={patchFileResourceShell}
                    isJourneyResource={isJourneyResource}
                  />
                </>
              )}

              <InputLabel for="name" required>
                {lexicon.resource} Name
              </InputLabel>
              <TextInput
                name="name"
                id="name"
                placeholder={lexicon.resource + ' Name'}
                value={resource.name}
                onChange={changeHandler}
              />

              <InputLabel for="description">{lexicon.resource} Description</InputLabel>
              <TextareaAutosizeStyled
                name="description"
                id="description"
                placeholder={lexicon.resource + ' Description'}
                value={resource.description}
                onChange={changeHandler}
                isJourney={isJourneyResource}
              />
              {isJourneyResource && <MaxCharactersWrapper><MaxCharacters>Maximum 80 characters</MaxCharacters></MaxCharactersWrapper>}
            </FormInner>
          </form>
        </ModalBottom>
      </ModalWrapper>
    </Modal>
  )
}

export default withApiData(AddResourceModal)
